import React, { useCallback, useEffect, useState } from 'react';
import { adminListOrders, adminUpdateOrderStage, adminDeleteOrder } from '../../lib/adminApi';
import { ShoppingBag, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const STAGES = [
  'Order received',
  'Payment confirmed',
  'Packed & shipped',
  'Out for delivery',
  'Delivered',
];

const stageBadge = (s) => {
  const map = {
    'Order received': 'bg-blue-500/15 text-blue-400',
    'Payment confirmed': 'bg-indigo-500/15 text-indigo-400',
    'Packed & shipped': 'bg-amber-500/15 text-amber-400',
    'Out for delivery': 'bg-orange-500/15 text-orange-400',
    'Delivered': 'bg-green-500/15 text-green-400',
  };
  return map[s] || 'bg-zinc-500/15 text-zinc-400';
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminListOrders()
      .then(setOrders)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStage = async (orderId, stage) => {
    await adminUpdateOrderStage(orderId, stage);
    load();
  };

  const handleDelete = async (orderId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(
      `Delete order ${orderId}?\n\nThis permanently removes the order from the database and cannot be undone.`
    )) return;
    try {
      await adminDeleteOrder(orderId);
      if (expanded === orderId) setExpanded(null);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not delete order');
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center gap-3 mb-1">
        <ShoppingBag className="text-[#909090]" size={26}/>
        <h1 className="text-3xl font-bold">Orders</h1>
      </div>
      <p className="text-zinc-500 text-sm mb-6">{orders.length} total order{orders.length === 1 ? '' : 's'}</p>

      {loading ? (
        <div className="py-20 text-center text-zinc-500">Loading...</div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center text-zinc-500 bg-zinc-950 border border-zinc-900 rounded-lg">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-zinc-950 border border-zinc-900 rounded-lg">
              <button
                type="button"
                onClick={() => setExpanded(expanded === o.orderId ? null : o.orderId)}
                className="w-full p-5 grid grid-cols-1 md:grid-cols-[1fr_1.5fr_auto_auto_auto] gap-4 items-center text-left"
              >
                <div>
                  <p className="font-mono font-semibold text-[#909090]">{o.orderId}</p>
                  <p className="text-xs text-zinc-500">{new Date(o.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-white">{o.firstName} {o.lastName}</p>
                  <p className="text-xs text-zinc-500">{o.email}</p>
                </div>
                <div className="text-sm text-zinc-400">{o.items?.length || 0} item(s)</div>
                <span className={`px-2.5 py-1 rounded text-xs font-medium justify-self-start ${stageBadge(o.stage)}`}>{o.stage}</span>
                <div className="flex items-center gap-3 justify-self-end">
                  <span className="text-lg font-bold text-[#909090]">€ {o.total.toFixed(2)}</span>
                  {expanded === o.orderId ? <ChevronUp size={18} className="text-zinc-500"/> : <ChevronDown size={18} className="text-zinc-500"/>}
                </div>
              </button>
              {expanded === o.orderId && (
                <div className="px-5 pb-5 border-t border-zinc-900 pt-4 grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-zinc-500 mb-2">Shipping</h3>
                    <p className="text-sm text-zinc-300">{o.firstName} {o.lastName}</p>
                    <p className="text-sm text-zinc-400">{o.address}</p>
                    <p className="text-sm text-zinc-400">{o.city}, {o.zip}</p>
                    <p className="text-sm text-zinc-400">{o.country}</p>
                    <p className="text-sm text-zinc-500 mt-2">Payment: <span className="text-zinc-300 capitalize">{o.payment}</span></p>

                    <h3 className="text-sm uppercase tracking-wider text-zinc-500 mt-5 mb-2">Update stage</h3>
                    <select value={o.stage} onChange={(e) => updateStage(o.orderId, e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded focus:outline-none focus:border-[#909090]">
                      {STAGES.map((s) => <option key={s}>{s}</option>)}
                    </select>

                    <h3 className="text-sm uppercase tracking-wider text-zinc-500 mt-5 mb-2">Danger zone</h3>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(o.orderId, e)}
                      className="inline-flex items-center gap-2 border border-red-900/50 text-red-400 hover:bg-red-900/20 hover:border-red-500 px-3 py-2 rounded text-sm transition-colors"
                    >
                      <Trash2 size={14}/> Delete order
                    </button>
                  </div>
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-zinc-500 mb-2">Items</h3>
                    <ul className="divide-y divide-zinc-900">
                      {o.items?.map((it, i) => (
                        <li key={i} className="py-2 flex items-center gap-3">
                          {it.image && <img src={it.image} alt={it.name} className="w-10 h-10 object-contain bg-black rounded"/>}
                          <div className="flex-1">
                            <p className="text-sm text-white">{it.name}</p>
                            <p className="text-xs text-zinc-500">Qty {it.qty} × € {it.price.toFixed(2)}</p>
                          </div>
                          <span className="text-sm text-[#909090]">€ {(it.qty * it.price).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-zinc-900 mt-3 pt-3 text-sm space-y-1">
                      <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>€ {o.subtotal.toFixed(2)}</span></div>
                      <div className="flex justify-between text-green-400"><span>Discount</span><span>− € {o.discount.toFixed(2)}</span></div>
                      {o.shipping ? (
                        <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>€ {o.shipping.toFixed(2)}</span></div>
                      ) : null}
                      <div className="flex justify-between text-white font-bold border-t border-zinc-900 pt-2 mt-2"><span>Total</span><span className="text-[#909090]">€ {o.total.toFixed(2)}</span></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
